import React, { useState, useEffect } from 'react';
import {
  RiFolder3Line,
  RiFolderOpenLine,
  RiArrowRightSLine,
  RiFolderAddLine,
  RiDraggable,
  RiEditLine,
  RiDeleteBinLine,
  RiErrorWarningFill,
  RiDownloadLine,
  RiMagicLine,
  RiLoader4Line,
} from 'react-icons/ri';
import FolderModal from './FolderModal';
import useFolderManagement from '@/hooks/useFolderManagement';
import { formatFolderNameWithVisaTypes } from '@/utils';
import Tooltip from '@/components/ui/Tooltip';
import { analytics } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from '@/utils/analytics-events';
import { DocumentsApi, CasesApi } from '@/api';

interface Folder {
  id: string;
  name: string;
  path: string;
  depth: number;
  children: Folder[];
}

interface FolderTreeEnhancedProps {
  caseId: string;
  folders: Folder[];
  onSelect: (fullPath: string | null) => void;
  selectedPath: string | null;
  className?: string;
  expandedPaths?: Set<string>;
  onToggle?: (path: string, isOpen: boolean) => void;
  onRefresh?: () => void;
}

const sortNodes = (nodes: any[]) =>
  nodes.sort((a, b) => a.name.localeCompare(b.name));

const TreeNode: React.FC<{
  node: Folder;
  depth: number;
  onSelect: (p: string | null) => void;
  selectedPath: string | null;
  expandedPaths: Set<string>;
  onToggle: (path: string, isOpen: boolean) => void;
  isManual?: boolean;
  folderId?: string;
  onRename?: (folderId: string, currentName: string) => void;
  onDelete?: (folderId: string, name: string) => void;
  onDownload?: (folderId: string, name: string) => void;
  onDragOver?: (folderId: string | null) => void;
  dragOverFolderId?: string | null;
  onFileDrop?: (folderId: string | null, documentId: string) => void;
  onFolderDragStart?: (folderId: string) => void;
  onFolderDrop?: (
    targetFolderId: string | null,
    draggedFolderId: string
  ) => void;
  draggedFolderId?: string | null;
}> = ({
  node,
  depth,
  onSelect,
  selectedPath,
  expandedPaths,
  onToggle,
  isManual = false,
  folderId,
  onRename,
  onDelete,
  onDownload,
  onDragOver,
  dragOverFolderId,
  onFileDrop,
  onFolderDragStart,
  onFolderDrop,
  draggedFolderId,
}) => {
  const hasChildren =
    'children' in node && typeof node.children === 'object'
      ? Array.isArray(node.children)
        ? node.children.length > 0
        : Object.keys(node.children).length > 0
      : false;

  const nodePath = node.path || `depth_${depth}`;
  const isRoot = depth === 0 && (nodePath === '' || node.name === 'All');
  const shouldBeOpen = isRoot || expandedPaths.has(nodePath);
  const [open, setOpen] = useState(shouldBeOpen);

  useEffect(() => {
    setOpen(isRoot || expandedPaths.has(nodePath));
  }, [expandedPaths, nodePath, isRoot]);

  const isSelected = selectedPath === nodePath;
  // Only show "All" for actual root node, not for regular folders
  const displayName = isRoot ? 'All' : formatFolderNameWithVisaTypes(node.name);
  const isDraggingThis = folderId && draggedFolderId === folderId;

  // Check if this is the "Other" folder (uncategorized/unsorted by AI)
  const isOtherFolder =
    (!isRoot &&
      (node.name?.toLowerCase() === 'other' ||
        node.name?.toLowerCase() === 'uncategorized' ||
        node.name?.toLowerCase() === 'unsorted')) ||
    node.name?.toLowerCase() === 'unrecognized';

  const childArray = hasChildren
    ? Array.isArray(node.children)
      ? sortNodes(node.children)
      : sortNodes(Object.values(node.children))
    : [];

  const computeAggregateCount = (n: any): number => {
    // Get count from documentCount field (for both manual and AI folders)
    const selfCount = n.documentCount || 0;

    // Also check for suggestions (legacy AI folders)
    const suggestionsCount =
      'suggestions' in n
        ? n.suggestions.reduce(
            (acc: number, s: any) => acc + (s.documentCount || 0),
            0
          )
        : 0;

    const children = Array.isArray(n.children)
      ? n.children
      : typeof n.children === 'object'
        ? Object.values(n.children)
        : [];

    return children.reduce(
      (sum: number, child: any) => sum + computeAggregateCount(child),
      Math.max(selfCount, suggestionsCount)
    );
  };

  const folderDocCount = computeAggregateCount(node);
  // For root node, folderId is undefined, but we still want to show drag over state when dragging
  const isDragOver =
    (isRoot && dragOverFolderId === null && draggedFolderId !== null) ||
    (folderId && dragOverFolderId === folderId && !isDraggingThis);

  const handleDragOver = (e: React.DragEvent) => {
    // Allow drag over for both regular folders and root
    if (!onFileDrop) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (onDragOver) onDragOver(folderId || null);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDragOver) onDragOver(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDragOver) onDragOver(null);

    const documentId = e.dataTransfer.getData('documentId');
    const draggedFolder = e.dataTransfer.getData('folderId');

    if (documentId && onFileDrop) {
      // Drop document into this folder (or root if folderId is null)
      onFileDrop(folderId || null, documentId);
    } else if (draggedFolder && onFolderDrop) {
      // Drop folder into this folder (make it a child, or move to root if folderId is null)
      onFolderDrop(folderId || null, draggedFolder);
    }
  };

  const handleFolderDragStart = (e: React.DragEvent) => {
    if (!folderId || !onFolderDragStart) return;
    e.stopPropagation();
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('folderId', folderId);
    onFolderDragStart(folderId);
  };

  const handleFolderDragEnd = (e: React.DragEvent) => {
    e.stopPropagation();
    if (onDragOver) onDragOver(null);
  };

  return (
    <div className={isRoot ? '' : 'mt-1'}>
      <div
        draggable={isManual && folderId && !isRoot ? true : false}
        onDragStart={
          isManual && folderId && !isRoot ? handleFolderDragStart : undefined
        }
        onDragEnd={
          isManual && folderId && !isRoot ? handleFolderDragEnd : undefined
        }
        className={`flex items-center text-sm cursor-pointer rounded px-1 py-0.5 group transition-colors border-2 select-none ${
          (isRoot && selectedPath === null) || (!isRoot && isSelected)
            ? isOtherFolder
              ? 'bg-amber-100 text-amber-800 border-transparent'
              : 'bg-blue-100 text-blue-700 border-transparent'
            : isDragOver
              ? 'bg-green-100 border-green-400 border-dashed'
              : isDraggingThis
                ? 'opacity-75 border-transparent'
                : isOtherFolder
                  ? 'bg-amber-50 text-amber-700 border-transparent hover:bg-amber-100'
                  : 'hover:bg-gray-100 text-gray-700 border-transparent'
        }`}
        onClick={() => {
          if (isRoot) {
            onSelect(null);
          } else {
            onSelect(nodePath || null);
          }
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {hasChildren && !isRoot ? (
          <button
            type="button"
            className="mr-1 text-gray-500 hover:text-gray-700"
            onClick={e => {
              e.stopPropagation();
              const newState = !open;
              setOpen(newState);
              onToggle(nodePath, newState);
            }}
            onMouseDown={e => e.stopPropagation()}
            aria-label={open ? 'Collapse' : 'Expand'}
          >
            <RiArrowRightSLine
              className={`w-4 h-4 transition-transform duration-200 ${
                open ? 'rotate-90' : ''
              }`}
            />
          </button>
        ) : (
          <span className="mr-1 w-4 h-4 inline-block" />
        )}
        {isManual && folderId && !isRoot && (
          <span
            className="mr-1 cursor-move text-gray-400 hover:text-gray-600"
            title="Drag to move folder"
          >
            <RiDraggable className="w-3 h-3" />
          </span>
        )}
        {hasChildren && !isRoot ? (
          <button
            type="button"
            className="mr-0.5 text-gray-600 hover:text-gray-800 flex-shrink-0"
            onClick={e => {
              e.stopPropagation();
              const newState = !open;
              setOpen(newState);
              onToggle(nodePath, newState);
            }}
            onMouseDown={e => e.stopPropagation()}
            aria-label={open ? 'Collapse' : 'Expand'}
          >
            {open ? (
              <RiFolderOpenLine className="w-4 h-4" />
            ) : (
              <RiFolder3Line className="w-4 h-4" />
            )}
          </button>
        ) : hasChildren && isRoot ? (
          <RiFolderOpenLine className="mr-1.5 w-4 h-4 text-gray-600 flex-shrink-0" />
        ) : (
          <RiFolder3Line className="mr-1.5 w-4 h-4 text-gray-600 flex-shrink-0" />
        )}
        <Tooltip content={displayName} side="right" delay={400}>
          <span className="truncate flex-1 flex items-center gap-1">
            {isOtherFolder && (
              <Tooltip
                content="Files that AI couldn't categorize. Drag documents to correct folders or use the move menu."
                side="right"
              >
                <RiErrorWarningFill className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
              </Tooltip>
            )}
            <span className="truncate" title={displayName}>{displayName}</span>
            {folderDocCount > 0 && (
              <span
                className={`ml-1 text-xs flex-shrink-0 ${isOtherFolder ? 'text-amber-600 font-semibold' : 'text-gray-500'}`}
              >
                ({folderDocCount})
              </span>
            )}
          </span>
        </Tooltip>
        {isManual && folderId && (
          <div
            onClick={e => e.stopPropagation()}
            className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            {onRename && (
              <Tooltip content="Rename folder" side="top">
                <button
                  type="button"
                  onClick={() => onRename(folderId, node.name)}
                  onMouseDown={e => e.stopPropagation()}
                  className="p-1 rounded hover:bg-blue-50 text-gray-600 hover:text-blue-700"
                >
                  <RiEditLine className="w-3.5 h-3.5" />
                </button>
              </Tooltip>
            )}
            {onDownload && folderDocCount > 0 && (
              <Tooltip content="Download folder as ZIP" side="top">
                <button
                  type="button"
                  onClick={() => onDownload(folderId, node.name)}
                  onMouseDown={e => e.stopPropagation()}
                  className="p-1 rounded hover:bg-green-50 text-gray-600 hover:text-green-700"
                >
                  <RiDownloadLine className="w-3.5 h-3.5" />
                </button>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip
                content={
                  folderDocCount > 0
                    ? 'Move documents to another folder before deleting'
                    : 'Delete folder'
                }
                side="top"
              >
                <button
                  type="button"
                  onClick={() => onDelete(folderId, node.name)}
                  onMouseDown={e => e.stopPropagation()}
                  disabled={folderDocCount > 0}
                  className={`p-1 rounded ${
                    folderDocCount > 0
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'hover:bg-red-50 text-gray-600 hover:text-red-700'
                  }`}
                >
                  <RiDeleteBinLine className="w-3.5 h-3.5" />
                </button>
              </Tooltip>
            )}
          </div>
        )}
      </div>
      {open && childArray.length > 0 && (
        <div className="ml-4">
          {childArray.map((child: any) => (
            <TreeNode
              key={child.id || child.fullPath}
              node={child}
              depth={depth + 1}
              onSelect={onSelect}
              selectedPath={selectedPath}
              expandedPaths={expandedPaths}
              onToggle={onToggle}
              isManual={isManual || !!child.id}
              folderId={child.id}
              onRename={onRename}
              onDelete={onDelete}
              onDownload={onDownload}
              onDragOver={onDragOver}
              dragOverFolderId={dragOverFolderId}
              onFileDrop={onFileDrop}
              onFolderDragStart={onFolderDragStart}
              onFolderDrop={onFolderDrop}
              draggedFolderId={draggedFolderId}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const collectAllPaths = (
  node: any,
  depth: number = 0,
  paths: Set<string> = new Set()
): Set<string> => {
  const nodePath = node.fullPath || node.path || `depth_${depth}`;
  paths.add(nodePath);

  const children = Array.isArray(node.children)
    ? node.children
    : typeof node.children === 'object'
      ? Object.values(node.children)
      : [];

  children.forEach((child: any) => collectAllPaths(child, depth + 1, paths));
  return paths;
};

const FolderTreeEnhanced: React.FC<FolderTreeEnhancedProps> = ({
  caseId,
  folders = [],
  onSelect,
  selectedPath,
  className,
  expandedPaths: externalExpandedPaths,
  onToggle: externalOnToggle,
  onRefresh,
}) => {
  const [internalExpandedPaths, setInternalExpandedPaths] = useState<
    Set<string>
  >(new Set([''])); // Start with just root expanded

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    mode: 'create' | 'rename';
    parentId?: string;
    folderId?: string;
    initialName?: string;
    parentName?: string;
  }>({
    isOpen: false,
    mode: 'create',
  });

  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  const [draggedFolderId, setDraggedFolderId] = useState<string | null>(null);
  const [movingDocumentId, setMovingDocumentId] = useState<string | null>(null);
  const [isReorganizing, setIsReorganizing] = useState(false);

  // Expand all folders when folders data changes
  useEffect(() => {
    if (!externalExpandedPaths && folders.length > 0) {
      const allPaths = new Set<string>(['']); // Root
      folders.forEach(folder => {
        collectAllPaths(folder, 0, allPaths);
      });
      setInternalExpandedPaths(allPaths);
    }
  }, [folders, externalExpandedPaths]);

  const folderManagement = useFolderManagement({
    caseId,
    onSuccess: () => {
      setModalState({ isOpen: false, mode: 'create' });
      if (onRefresh) onRefresh();
    },
    onError: error => {
      console.error('Folder operation error:', error);
    },
  });

  const expandedPaths = externalExpandedPaths || internalExpandedPaths;

  const handleToggle = (path: string, isOpen: boolean) => {
    if (externalOnToggle) {
      externalOnToggle(path, isOpen);
    } else {
      setInternalExpandedPaths(prev => {
        const next = new Set(prev);
        if (isOpen) {
          next.add(path);
        } else {
          next.delete(path);
        }
        return next;
      });
    }
  };

  const handleCreateRootFolder = () => {
    setModalState({
      isOpen: true,
      mode: 'create',
      parentId: undefined,
      parentName: 'Root',
    });
  };

  const handleAIReorganize = async () => {
    if (isReorganizing) return;

    const confirmed = window.confirm(
      'AI will analyze all documents and reorganize folders:\n\n' +
      '• Merge duplicate/similar folders\n' +
      '• Move documents to appropriate folders\n' +
      '• Clean up empty folders\n\n' +
      'This may take a moment. Continue?'
    );

    if (!confirmed) return;

    setIsReorganizing(true);
    try {
      await CasesApi.reorganizeFolders(caseId);
      analytics.track(ANALYTICS_EVENTS.FOLDER_AI_REORGANIZE, {
        case_id: caseId,
      });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('AI reorganization failed:', error);
      alert('Failed to reorganize folders. Please try again.');
    } finally {
      setIsReorganizing(false);
    }
  };

  const handleRename = (folderId: string, currentName: string) => {
    // Extract just the folder name (last segment if it contains path separators)
    const folderName = currentName.includes('/')
      ? currentName.split('/').pop()?.trim() || currentName
      : currentName;

    setModalState({
      isOpen: true,
      mode: 'rename',
      folderId,
      initialName: folderName,
      parentName: currentName, // Keep full path for display
    });
  };

  const handleFolderDrop = async (
    targetFolderId: string | null,
    draggedFolder: string
  ) => {
    if (draggedFolder === targetFolderId) return; // Can't drop on itself
    await folderManagement.moveFolder(draggedFolder, targetFolderId);
    setDraggedFolderId(null);
  };

  const handleDelete = async (folderId: string, name: string) => {
    if (
      window.confirm(`Are you sure you want to delete the folder "${name}"?`)
    ) {
      await folderManagement.deleteFolder(folderId);
      analytics.track(ANALYTICS_EVENTS.FOLDER_DELETED, {
        case_id: caseId,
        folder_id: folderId,
        folder_name: name,
      });
    }
  };

  const handleDownload = async (folderId: string, name: string) => {
    try {
      const { blob, filename } = await DocumentsApi.downloadFolder(
        caseId,
        folderId
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download folder:', error);
      alert('Failed to download folder. Please try again.');
    }
  };

  const handleModalSubmit = async (name: string) => {
    if (modalState.mode === 'create') {
      await folderManagement.createFolder(name, modalState.parentId);
      analytics.track(ANALYTICS_EVENTS.FOLDER_CREATED, {
        case_id: caseId,
        folder_name: name,
        parent_folder_id: modalState.parentId,
      });
    } else if (modalState.mode === 'rename' && modalState.folderId) {
      await folderManagement.renameFolder(modalState.folderId, name);
      analytics.track(ANALYTICS_EVENTS.FOLDER_RENAMED, {
        case_id: caseId,
        folder_id: modalState.folderId,
        new_name: name,
      });
    }
  };

  // Create root "All" node with folders as children
  const rootNode: any = React.useMemo(() => {
    return {
      name: 'All',
      fullPath: '',
      children: folders, // Folders are children of root
      suggestions: [],
    };
  }, [folders]);

  return (
    <div className={`text-xs ${className}`}>
      <div className="font-semibold text-gray-700 mb-3 px-1 flex items-center justify-between">
        <span>Folders</span>
        <div className="flex items-center gap-2">
          <Tooltip content="AI reorganize folders" side="bottom" delay={400}>
            <button
              type="button"
              onClick={handleAIReorganize}
              disabled={isReorganizing}
              className={`flex items-center gap-1 text-xs font-normal transition-colors ${
                isReorganizing
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-purple-600 hover:text-purple-700'
              }`}
              title="AI reorganize folders"
            >
              {isReorganizing ? (
                <RiLoader4Line className="w-4 h-4 animate-spin" />
              ) : (
                <RiMagicLine className="w-4 h-4" />
              )}
            </button>
          </Tooltip>
          <button
            type="button"
            onClick={handleCreateRootFolder}
            className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-xs font-normal"
            title="Create new folder"
          >
            <RiFolderAddLine className="w-4 h-4" />
            New
          </button>
        </div>
      </div>
      <div>
        {/* Root "All" node with folders as children */}
        <TreeNode
          node={rootNode}
          depth={0}
          onSelect={onSelect}
          selectedPath={selectedPath}
          expandedPaths={expandedPaths}
          onToggle={handleToggle}
          onDragOver={setDragOverFolderId}
          dragOverFolderId={dragOverFolderId}
          onFileDrop={async (folderId, documentId) => {
            try {
              setMovingDocumentId(documentId);
              await folderManagement.moveFile(documentId, folderId);

              analytics.track(ANALYTICS_EVENTS.DOCUMENT_MOVED_TO_FOLDER, {
                case_id: caseId,
                document_id: documentId,
                target_folder_id: folderId,
              });

              // Success feedback could be added here if needed
            } catch (error) {
              console.error('Failed to move document:', error);
              alert('Failed to move document. Please try again.');
            } finally {
              setMovingDocumentId(null);
            }
          }}
          onFolderDragStart={setDraggedFolderId}
          onFolderDrop={handleFolderDrop}
          draggedFolderId={draggedFolderId}
          onRename={handleRename}
          onDelete={handleDelete}
          onDownload={handleDownload}
        />
      </div>

      <FolderModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ isOpen: false, mode: 'create' })}
        onSubmit={handleModalSubmit}
        mode={modalState.mode}
        initialName={modalState.initialName}
        parentFolderName={modalState.parentName}
        loading={folderManagement.loading}
      />
    </div>
  );
};

export default FolderTreeEnhanced;
